/** @module "Mehr": everything the five phone tabs have no room for, as one list. */
import { Link } from "@tanstack/react-router";
import {
  Archive,
  ChevronRight,
  Database,
  Images,
  List,
  LogOut,
  MessageSquareText,
  Package,
  RefreshCw,
  ScanSearch,
  Search,
  Settings,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/page-header";
import { useOpenReviews } from "@/lib/review-count";
import { signOut } from "@/lib/session";
import { useOpenFeedback } from "@/lib/feedback-count";
import { useSession } from "@/lib/use-session";

function Row({
  to,
  search,
  icon,
  label,
  end,
}: {
  to: string;
  search?: Record<string, string>;
  icon: ReactNode;
  label: string;
  end?: ReactNode;
}) {
  return (
    <Link to={to} search={search} className="menu-row">
      {icon}
      <span className="grow">{label}</span>
      {end ?? <ChevronRight className="size-5 text-(--text-muted)" />}
    </Link>
  );
}

export function MorePage() {
  const { t } = useTranslation();
  const session = useSession();
  const open = useOpenReviews(true);
  const admin = session.account?.role === "admin";
  const unread = useOpenFeedback(admin);

  return (
    <div className="flex flex-col">
      <PageHeader title={t("nav.more")} global={false} />
      <Row
        to="/review"
        icon={<List />}
        label={t("nav.reviewQueue")}
        end={open > 0 ? <span className="badge badge-count">{open}</span> : undefined}
      />
      <Row to="/search" icon={<Search />} label={t("nav.search")} />
      <Row
        to="/collection"
        search={{ status: "archived" }}
        icon={<Archive />}
        label={t("more.archive")}
      />
      <Row to="/locations" icon={<Package />} label={t("nav.locations")} />
      <Row to="/settings" icon={<Settings />} label={t("nav.settings")} />
      {admin ? (
        <>
          <span className="t-label pt-5 pb-1">{t("nav.adminSection")}</span>
          <Row
            to="/admin"
            search={{ tab: "jobs" }}
            icon={<RefreshCw />}
            label={t("nav.adminJobs")}
          />
          <Row
            to="/admin"
            search={{ tab: "recognition" }}
            icon={<ScanSearch />}
            label={t("nav.adminRecognition")}
          />
          <Row to="/admin" search={{ tab: "users" }} icon={<Users />} label={t("nav.adminUsers")} />
          <Row
            to="/admin"
            search={{ tab: "scans" }}
            icon={<Images />}
            label={t("nav.adminScans")}
          />
          <Row
            to="/admin"
            search={{ tab: "feedback" }}
            icon={<MessageSquareText />}
            label={t("nav.adminFeedback")}
            end={unread > 0 ? <span className="badge badge-count">{unread}</span> : undefined}
          />
          <Row
            to="/admin"
            search={{ tab: "system" }}
            icon={<Database />}
            label={t("nav.adminStatus")}
          />
        </>
      ) : null}
      <button
        type="button"
        className="menu-row mt-6 border-b-0 text-left"
        onClick={() => {
          void signOut();
        }}
      >
        <LogOut />
        <span className="grow">{t("auth.signOut")}</span>
      </button>
    </div>
  );
}
